import { ContactMessage, Model } from "@lumora/core";
import { PackageCatalog, type PackageId } from "./PackageCatalog.js";

export const CATEGORIES = [
  "Amazon seller product",
  "TikTok Shop product",
  "Etsy or handmade product",
  "Shopify or own-brand product",
  "Property or rental (3D tour waitlist)",
  "Something else",
] as const;

export const BUDGETS = ["Under $100", "$100 to $300", "$300 to $750", "$750 or more"] as const;

export interface QuoteRequestInit {
  name: string;
  email: string;
  category: string;
  budget: string;
  packageId: PackageId | "";
  skuCount: number;
  details: string;
}

const catalog = PackageCatalog.launch();

/** A seller's quote request. Composes the contact message body the backend stores. */
export class QuoteRequest extends Model {
  name: string;
  email: string;
  category: string;
  budget: string;
  packageId: PackageId | "";
  skuCount: number;
  details: string;

  constructor(init: QuoteRequestInit) {
    super();
    this.name = init.name;
    this.email = init.email;
    this.category = init.category;
    this.budget = init.budget;
    this.packageId = init.packageId;
    this.skuCount = init.skuCount;
    this.details = init.details;
  }

  /** Launch-price estimate in USD, or null when no package fits the quantity. */
  estimateUsd(): number | null {
    if (this.packageId === "") return null;
    try {
      return catalog.get(this.packageId).total(this.skuCount);
    } catch {
      return null;
    }
  }

  toMessageBody(): string {
    const pkg = this.packageId === "" ? "Not sure yet" : catalog.get(this.packageId).name;
    const est = this.estimateUsd();
    const lines = [
      "Quote request",
      `Category: ${this.category}`,
      `Budget: ${this.budget}`,
      `Package: ${pkg}`,
      `Products: ${this.skuCount}`,
    ];
    if (est !== null) lines.push(`Launch-price estimate: $${est}`);
    lines.push("", this.details.trim());
    return lines.join("\n");
  }

  toContactMessage(): ContactMessage {
    return new ContactMessage(this.name, this.email, this.toMessageBody());
  }

  validate(): string[] {
    const errors: string[] = [];
    if (this.category === "") errors.push("Choose a product category.");
    if (this.budget === "") errors.push("Choose a budget range.");
    if (!Number.isInteger(this.skuCount) || this.skuCount < 1) errors.push("Enter how many products (1 or more).");
    if (this.details.trim().length < 10) errors.push("Tell us a little about the product (10+ characters).");
    errors.push(...new ContactMessage(this.name, this.email, "x".repeat(10)).validate());
    return errors;
  }
}
